import {
  DATAOWNER,
  EMPLOYEE_V2,
  LOCATION_V2,
  LOCATION_V2_EMPLOYEES,
} from '@dap/memberadmin/config';
import { Dataowner, InformationModelType } from '@dap/memberadmin/types';
import { messages } from '@dap/common/i18n';
import { getAcceptLanguage, mapOrgUnitRolesToOldRoles, roleBasicMapper } from '@dap/common/utils';
import { EmployeeBasicDTO, EmployeeDetailDTO, LocationDetailDTO } from '@dap/generated-types';
import { bearerToken, postRequest } from '@shared/fetch-utils';
import { catchAndLogError, reportReduxActionException } from '@shared/reporting';
import { AuthState } from '@shared/state/auth';
import { t } from 'i18next';
import { Epic, StateObservable, combineEpics, ofType } from 'redux-observable';
import { Observable, catchError, concatMap, iif, map, of, switchMap } from 'rxjs';
import { brandApi } from '../../api/brandApi';
import { dataownerApi } from '../../api/dataownerApi';
import { locationApi } from '../../api/locationApi';
import { BrandTags, DataownerTags, LocationTags } from '../../api/tags';
import { BrandState } from '../brand/brandReducer';
import {
  LocationState,
  createNewLocationAddAdminFailure,
  createNewLocationFailure,
  createNewLocationRequest,
  createNewLocationSuccess,
} from './locationReducer';

interface LocationRootState {
  location: LocationState;
  auth: AuthState;
  brand: BrandState;
}

const iamHeaders = (state$: StateObservable<LocationRootState>) => ({
  ...bearerToken(state$.value.auth.token),
  'Accept-Language': getAcceptLanguage(),
});

const createLocationWizardEpic: Epic = (
  actions$: Observable<ReturnType<typeof createNewLocationRequest>>,
  state$: StateObservable<LocationRootState>
) =>
  actions$.pipe(
    ofType(createNewLocationRequest.type),
    // either use exisiting d.o or create new
    concatMap((action) =>
      iif(
        () => !!action.payload.dataowner.key, // is either existing d.o with key or not
        of(null),
        postRequest<Dataowner>(DATAOWNER, iamHeaders(state$), action.payload.dataowner).pipe(
          map(({ response }) => response)
        ) // exposes the response in an observable
      ).pipe(
        // use either new d.o or exisisting for new location
        concatMap((newDataowner) =>
          postRequest<LocationDetailDTO>(LOCATION_V2, iamHeaders(state$), {
            ...action.payload.location,
            dataowner: { key: newDataowner?.key || action.payload.dataowner.key },
          }).pipe(
            concatMap(({ response: newLocation }) => {
              // if admin is set
              const adminRole =
                newLocation.availableRoles?.find(
                  (role) => role.type === InformationModelType.Location && role.role?.isAdmin
                ) || newLocation.availableRoles?.[0];

              const mappedRole = adminRole
                ? mapOrgUnitRolesToOldRoles(roleBasicMapper([adminRole]))
                : [];

              const adminPayload = {
                ...action.payload.admin,
                locations: [newLocation.key],
                roles: mappedRole,
              };

              const existingUserPayload = {
                userId: action.payload.admin.id,
                apps: action.payload.admin.apps,
                roles: mappedRole,
              };
              // if not skipped, add admin by either using existing user or create new
              const adminRequest$ = iif(
                () => !!action.payload.admin.givenName,
                iif(
                  () => !!action.payload.admin.id,
                  postRequest<EmployeeBasicDTO[]>(
                    LOCATION_V2_EMPLOYEES(newLocation.key),
                    iamHeaders(state$),
                    [existingUserPayload]
                  ),
                  postRequest<EmployeeDetailDTO>(EMPLOYEE_V2, iamHeaders(state$), adminPayload)
                ),
                of(null)
              ).pipe(
                // error, adding admin. Returns new observable with action of failure
                catchError((error) => {
                  const message = t(`location:${messages.location.create.error.location.admin}`);
                  reportReduxActionException(action, {
                    ...error,
                    message,
                  });
                  return of(createNewLocationAddAdminFailure({ error: message }));
                })
              );
              return adminRequest$.pipe(
                // result of admin request, and success create location
                switchMap((adminResponse) => {
                  const errors = [];
                  if (adminResponse?.type === 'location/createNewLocationAddAdminFailure') {
                    errors.push(adminResponse.payload.error);
                  }
                  return of(
                    createNewLocationSuccess({ location: newLocation, chainedErrors: errors }),
                    brandApi.util.invalidateTags([BrandTags.BRAND_LOCATIONS]),
                    dataownerApi.util.invalidateTags([
                      DataownerTags.SEARCH_DATAOWNERS,
                      DataownerTags.DATAOWNER,
                    ]),
                    locationApi.util.invalidateTags([LocationTags.SEARCH_LOCATIONS]),
                    brandApi.util.invalidateTags([BrandTags.BRAND_DATAOWNERS]),
                    dataownerApi.util.invalidateTags([DataownerTags.DATAOWNER])
                  );
                })
              );
            }),

            // error, could not create location
            catchError((error) => {
              reportReduxActionException(action, {
                ...error,
                message: t(`location:${messages.location.create.error.location.create}`, {
                  entity: t(`brand:${messages.brand.location.name.definite}`),
                }),
              });
              return of(createNewLocationFailure(error));
            })
          )
        ),

        // error, create d.o
        catchAndLogError(action, [
          createNewLocationFailure,
          () => brandApi.util.invalidateTags([BrandTags.BRAND_DATAOWNERS]),
          () => dataownerApi.util.invalidateTags([DataownerTags.SEARCH_DATAOWNERS]),
        ])
      )
    )
  );

export default combineEpics(createLocationWizardEpic);
